import { isEmpty, isBoolean, uniq, isNull, round } from "lodash";
import {
  singleValue,
  orderStatus,
  getFootageValue,
  isResetSelectionForFilter,
} from "../../../utils";
import { isItemDepartment } from "../../../utils/deptValidator";
import { resetPogFieldsOnSelection } from "./index";
import pogDataUpdater from "./pogDataUpdater";
import { groupBy } from "../DepartmentSearch";

export const prepareInitialData = (data = []) => {
  const updatedData = [];
  if (data.length) {
    for (let i = 0; i < data.length; i++) {
      const pog = data[i];
      updatedData.push({
        ...pog,
        ...(isItemDepartment(pog) && {
          ReleaseStatus:
            isEmpty(pog?.ReleaseStatus) && pog?.Selected === singleValue.yes
              ? orderStatus.notReleased
              : pog?.ReleaseStatus,
        }),
        selection: false,
        virtualLoading: true,
        PlanogramName: pog?.PlanogramName?.trim(),
        prevSelected: pog?.Selected,
        AdjLineal: isNull(pog?.AdjLineal)
          ? pog?.LinealFootage
            ? String(getFootageValue(pog?.LinealFootage, true)).trim()
            : getFootageValue(pog?.LinealFootage, true)
          : getFootageValue(pog?.AdjLineal, true),
        __original: {
          Selected: pog?.Selected,
        },
      });
    }
    return updatedData;
  }
  return data;
};

export const checkPogsBeforeSave = (
  dispatch,
  showNotifier,
  isSelected = true,
  pogList = [],
  selections = [],
  originalList = [],
  formData = null,
  isOtherAction = false
) => {
  let isValidSaveData = true;
  // If no 'Selected' field value preset in settings form.
  if (!isSelected) {
    for (let i = 0, len = pogList.length; i < len; i++) {
      const pog = pogList[i] || {};
      if (selections.includes(pog?.PlanogramNumber)) {
        const originalPog = originalList?.find(
          (i) => i?.PlanogramNumber === pog?.PlanogramNumber
        );
        let isValidDiff = true;
        if (
          isOtherAction &&
          ([singleValue.existing, singleValue.no].includes(
            originalPog?.Selected
          ) ||
            [singleValue.existing, singleValue.no].includes(pog?.Selected))
        ) {
          isValidDiff = false;
        }
        if (!isEmpty(formData?.selected)) {
          if (formData?.selected !== originalPog.Selected) {
            isValidDiff = true;
          }
        }
        if (
          (!isValidDiff &&
            !originalPog?.RetailerNotes &&
            !pog?.RetailerNotes &&
            !originalPog?.PlannerNotes &&
            !pog?.PlannerNotes) ||
          !isValidDiff
        ) {
          isValidSaveData = false;
          dispatch(
            showNotifier({
              message:
                "Few checked POGs are not saved or marked as selected 'N'/'E'. Please verify 'Sel' field.",
              severity: "error",
            })
          );
          break;
        }
      }
    }
  }
  return isValidSaveData;
};

export const prepareCreatePOData = (
  departmentFilters,
  storeId,
  formData,
  userId,
  selections = []
) => {
  let preparedPogs = {};
  if (selections.length) {
    preparedPogs = {
      storeId,
      departmentLetter: departmentFilters?.departmentLetter,
      vendorName: departmentFilters?.vendorName || null,
      skuNumber: departmentFilters?.skuNumber || null,
      poNumber: formData?.customPONumber, // Mandatory
      createPOBy:
        formData?.generatePOBy === "Department"
          ? "DEPT"
          : formData?.generatePOBy, // Mandatory
      userId: userId, // Mandatory
      pogNumberList: selections,
    };
  }
  return preparedPogs;
};

export const postCreatePOUpdateData = (pogs, details = [], selections = []) => {
  const preparedPogList = [];
  if (selections.length && details.length) {
    for (let i = 0, len = pogs.length; i < len; i++) {
      let pog = pogs[i] || {};
      if (selections.includes(pog?.PlanogramNumber)) {
        const pogWithPoNumber = details.find(
          (i) => i?.PogNumber === pog?.PlanogramNumber
        );
        preparedPogList.push({
          ...pog,
          PONumber: pogWithPoNumber?.PONumber,
        });
      }
    }
  }
  return preparedPogList;
};

export const prepareSettingsSaveData = (
  departmentFilters,
  details,
  storeId,
  userId,
  pogs = [],
  selections = []
) => {
  let updatedDetails = {
    storeId,
    departmentLetter: departmentFilters?.departmentLetter,
    vendorName: departmentFilters?.vendorName || null,
    skuNumber: departmentFilters?.skuNumber || null,
    userId,
    pogList: [],
  };
  if (pogs.length && selections.length) {
    for (let i = 0; i < pogs.length; i++) {
      const pog = pogs[i] || {};
      if (selections.includes(pog?.PlanogramNumber)) {
        updatedDetails = pogDataUpdater(updatedDetails, pog, details);
        if (selections.length === updatedDetails.pogList.length) {
          break;
        }
      }
    }
  }
  return updatedDetails;
};

export const postSettingsSaveUpdateData = (
  formData,
  pogs,
  updatedPogDetails,
  originalData,
  selections = [],
  skip = false,
  departmentFilters = {}
) => {
  const updatedPogs = [];
  for (let i = 0; i < pogs.length; i++) {
    let pog = pogs[i] || {};
    if (selections?.includes(pog?.PlanogramNumber)) {
      // If operation is for SKU or Vendor then no need to immediately update POG summary for input 5 fields.
      if (!skip) {
        if (!isEmpty(formData?.selected)) {
          pog = { ...pog, Selected: formData?.selected };
          pog = {
            ...resetPogFieldsOnSelection("Selected", pog, pog?.LinealFootage),
          };
        }

        if ([formData?.selected, pog?.Selected].includes(singleValue.yes)) {
          const originalPog = originalData?.find(
            (p) => p?.PlanogramNumber === pog?.PlanogramNumber
          );
          if (
            [singleValue.no, singleValue.existing].includes(
              originalPog?.Selected
            )
          ) {
            pog = {
              ...pog,
              ReleaseStatus: orderStatus.notReleased,
            };
          }
        }

        if (formData?.datingDays !== "" && pog.Selected === singleValue.yes) {
          pog = {
            ...pog,
            DatingDays: formData?.datingDays,
            CustomDatingDays: false,
          };
        }

        if (
          formData?.discountMultiplier !== "" &&
          pog.Selected === singleValue.yes
        ) {
          pog = {
            ...pog,
            DiscMultiplier: formData?.discountMultiplier,
            CustomDiscMultiplier: false,
          };
        }

        if (
          formData?.qtyMultiplier !== "" &&
          pog.Selected === singleValue.yes
        ) {
          pog = {
            ...pog,
            QtyMultiplier: formData?.qtyMultiplier,
            CustomQtyMultiplier: false,
          };
        }

        if (formData?.coreDiscount !== "" && pog.Selected === singleValue.yes) {
          pog = {
            ...pog,
            CoreDiscount: round(formData?.coreDiscount || 0, 2),
            CustomCoreDiscount: false,
          };
        }

        if (
          formData?.nonCoreDiscount !== "" &&
          pog.Selected === singleValue.yes
        ) {
          pog = {
            ...pog,
            NonCoreDiscount: round(formData?.nonCoreDiscount || 0, 2),
            CustomNonCoreDiscount: false,
          };
        }
      }

      // Update pog summary fetched from server end.
      const pogDetails = updatedPogDetails.find(
        (p) => p.PogNumber === pog.PlanogramNumber
      );
      let isResetSelection = false;
      if ([groupBy[4], groupBy[5]].includes(departmentFilters.viewBy)) {
        isResetSelection = isResetSelectionForFilter(
          departmentFilters,
          pogDetails?.AdjQty || 0
        );
      }

      const selectionVal =
        isResetSelection &&
        [groupBy[4], groupBy[5]].includes(departmentFilters.viewBy)
          ? singleValue.no
          : pog?.Selected;

      pog = {
        ...pog,
        Selected: selectionVal,
        prevSelected: selectionVal,
        AdjCost: pogDetails?.AdjCost,
        ExtCost: pogDetails?.ExtCost,
        AdjQty: pogDetails?.AdjQty,
        ...(skip && {
          DiscMultiplier: !pogDetails?.CustomDiscMultiplier
            ? formData?.discountMultiplier || 1
            : 1,
          QtyMultiplier: !pogDetails?.CustomQtyMultiplier
            ? formData?.qtyMultiplier || 1
            : 1,
          DatingDays: !pogDetails?.CustomDatingDays
            ? formData?.datingDays || 0
            : 0,
          CoreDiscount: !pogDetails?.CustomCoreDiscount
            ? formData?.coreDiscount || 0
            : 0,
          NonCoreDiscount: !pogDetails?.CustomQtyMultiplier
            ? formData?.nonCoreDiscount || 0
            : 0,
          // if dept. search is for SKU/VENDOR then it will crete
          // new value inconsistency hance setting these flags.
          CustomCoreDiscount: pogDetails?.CustomCoreDiscount,
          CustomDatingDays: pogDetails?.CustomDatingDays,
          CustomDiscMultiplier: pogDetails?.CustomDiscMultiplier,
          CustomNonCoreDiscount: pogDetails?.CustomNonCoreDiscount,
          CustomQtyMultiplier: pogDetails?.CustomQtyMultiplier,
        }),
        __original: {
          Selected: selectionVal,
        },
      };
      updatedPogs.push(pog);
    }
  }
  return updatedPogs;
};

export const prepareSaveDatesData = (
  departmentFilters,
  date,
  storeId,
  userDetails,
  selections = []
) => {
  let updatedData = {};
  if (selections.length) {
    updatedData = {
      storeId,
      departmentLetter: departmentFilters?.departmentLetter,
      vendorName: departmentFilters?.vendorName || null,
      skuNumber: departmentFilters?.skuNumber || null,
      releaseDate: date,
      userId: userDetails?.email,
      pogNumberList: selections,
    };
  }
  return updatedData;
};

export const postSaveDatesUpdateData = (
  date,
  pogs,
  selections = [],
  responsePogs = []
) => {
  const updatedPogs = [];
  for (let i = 0; i < pogs.length; i++) {
    let pog = pogs[i] || {};
    const _pog = responsePogs?.find(
      (p) => p?.PogNumber === pog?.PlanogramNumber
    );
    if (selections?.includes(pog?.PlanogramNumber)) {
      if (
        date !== "" &&
        pog.Selected === singleValue.yes &&
        _pog?.CountOfPOItems === 0 // If all items having same date then only show in POG UI then API will give 0 count.
      ) {
        pog = { ...pog, ReleaseDate: _pog?.ReleaseDate };
      }

      updatedPogs.push(pog);
    }
  }
  return updatedPogs;
};

export const prepareDeleteDatesData = (
  departmentFilters,
  storeId,
  userEmail,
  selections = []
) => {
  let updatedData = {};
  if (selections.length) {
    updatedData = {
      storeId,
      departmentLetter: departmentFilters?.departmentLetter,
      vendorName: departmentFilters?.vendorName || null,
      skuNumber: departmentFilters?.skuNumber || null,
      userId: userEmail,
      pogNumberList: selections,
    };
  }
  return updatedData;
};

export const postDeleteDatesUpdateData = (pogs, selections = []) => {
  const updatedPogs = [];
  for (let i = 0; i < pogs.length; i++) {
    let pog = pogs[i] || {};
    if (selections?.includes(pog?.PlanogramNumber)) {
      if (pog.Selected === singleValue.yes) {
        pog = {
          ...pog,
          ReleaseDate: null,
          ReleaseStatus: orderStatus.notReleased,
        };
      }
      updatedPogs.push(pog);
    }
  }
  return updatedPogs;
};

export const saveUpdatedPogs =
  (postSaveUpdatedPogs, isCreatePO = false) =>
  (oldPogDetails) => {
    let _updates = [];
    for (let i = 0, len = oldPogDetails?.length; i < len; i++) {
      const pog = oldPogDetails[i] || {};
      const updatedPogDetails = postSaveUpdatedPogs?.find(
        (updatedPog) => updatedPog?.PlanogramNumber === pog?.PlanogramNumber
      );
      if (updatedPogDetails) {
        _updates.push({
          ...updatedPogDetails,
          selection: isCreatePO ? updatedPogDetails.selection : false, // removing selections after saving data except create PO.
        });
      } else {
        _updates.push(pog);
      }
    }
    return _updates;
  };

export const postPogLevelUpdateData = (
  selectedPog,
  updatedPog,
  _originalPog,
  filteredData
) => {
  const postSaveUpdatedItems = filteredData.map((p) => {
    if (p?.PlanogramNumber === selectedPog?.PlanogramNumber) {
      return {
        ...p,
        ...selectedPog,
        selection: false,
        AdjCost: updatedPog?.AdjCost,
        ExtCost: updatedPog?.ExtCost,
        AdjQty: updatedPog?.AdjQty,
        ...(selectedPog?.Selected === singleValue.no && {
          ReleaseStatus: "",
          ReleaseDate: null,
          PONumber: "",
          CustomCoreDiscount: false, // This is from POG level hence we can directly mark these flags as false
          CustomDatingDays: false,
          CustomDiscMultiplier: false,
          CustomNonCoreDiscount: false,
          CustomQtyMultiplier: false,
        }),
        ...(selectedPog?.Selected === singleValue.yes && {
          ReleaseStatus: orderStatus.notReleased,
        }),
        ...(selectedPog?.Selected === singleValue.yes && {
          DiscMultiplier: !updatedPog?.CustomDiscMultiplier
            ? selectedPog?.DiscMultiplier || 1
            : 1,
          QtyMultiplier: !updatedPog?.CustomQtyMultiplier
            ? selectedPog?.QtyMultiplier || 1
            : 1,
          DatingDays: !updatedPog?.CustomDatingDays
            ? selectedPog?.DatingDays || 0
            : 0,
          CoreDiscount: !updatedPog?.CustomCoreDiscount
            ? selectedPog?.CoreDiscount || 0
            : 0,
          NonCoreDiscount: !updatedPog?.NonCoreDiscount
            ? selectedPog?.NonCoreDiscount || 0
            : 0,
          CustomCoreDiscount: updatedPog?.CustomCoreDiscount,
          CustomDatingDays: updatedPog?.CustomDatingDays,
          CustomDiscMultiplier: updatedPog?.CustomDiscMultiplier,
          CustomNonCoreDiscount: updatedPog?.CustomNonCoreDiscount,
          CustomQtyMultiplier: updatedPog?.CustomQtyMultiplier,
        }),
        __original: {
          Selected: selectedPog?.Selected,
        },
      };
    }
    return p;
  });
  return postSaveUpdatedItems;
};

export const updateDuplicatePogNames = ({
  field,
  value,
  id,
  PogName,
  setSelectedPogNames,
  isPogSelected,
  isPogChecked,
  originalData,
}) => {
  if (field === "selection" || field === "Selected") {
    let originalPog = {};
    // To prevent selected Y pog marked as duplicate.
    for (let h = 0, len = originalData.length; h < len; h++) {
      const _pog = originalData[h] || {};
      if (_pog?.PlanogramNumber === id) {
        originalPog = { ..._pog };
        break;
      }
    }
    if (originalPog?.Selected !== singleValue.yes) {
      if (
        (isBoolean(value) && value) ||
        [singleValue.yes, singleValue.existing].includes(value)
      ) {
        setSelectedPogNames((oldList) => uniq([...oldList, PogName]));
      } else {
        if (
          (field === "selection" &&
            !value &&
            [singleValue.no].includes(isPogSelected)) ||
          (field === "selection" && !value && !isPogChecked) ||
          (field === "Selected" &&
            [singleValue.no].includes(value) &&
            !isPogChecked)
        ) {
          setSelectedPogNames((oldList) =>
            oldList.filter((name) => name !== PogName)
          );
        }
      }
    }
  }
};

export const skipPogSummaryUpdate = (departmentFilters) => {
  return [groupBy[4], groupBy[5]].includes(departmentFilters?.viewBy) || false;
};
